import type { Apis } from '@efdigital/ll-addon-auth/types/apis';
import config from '@efdigital/ll-admin-v2/config/environment';
import { DataTypeLti } from '@efdigital/ll-admin-v2/data/data-types/lti';
import { LTI_API_PATH } from '@efdigital/ll-admin-v2/data/handlers/lti/lti-consuming-platform-handler.ts';
import type { LtiConsumingPlatform } from '@efdigital/ll-admin-v2/data/types/lti';
import { withReactiveResponse } from '@warp-drive/core/request';
import {
  createRecord,
  deleteRecord,
  updateRecord,
} from '@warp-drive/utilities/json-api';

import { LtiOperation } from './ops.ts';

const apis: Apis = config['ll-addon-auth-apis'];

function consumingPlatformRequestBody(platform: LtiConsumingPlatform) {
  return {
    name: platform.name,
    description: platform.description,
    clientId: platform.clientId,
    platformData: platform.platformData,
  };
}

export function buildListConsumingPlatforms(clientId: string) {
  const url = `${apis.systemsApi.host}${LTI_API_PATH}/platforms?clientId=${encodeURIComponent(clientId)}`;
  const cacheKey = url;

  return withReactiveResponse<LtiConsumingPlatform[]>({
    url,
    method: 'GET',
    headers: new Headers({
      'Content-Type': 'application/json',
    }),
    op: LtiOperation.ListConsumingPlatforms,
    cacheOptions: {
      key: cacheKey,
      types: [DataTypeLti.ConsumingPlatform],
    },
  });
}

export function buildFindConsumingPlatform(platformId: string) {
  const url = `${apis.systemsApi.host}${LTI_API_PATH}/platforms/${encodeURIComponent(platformId)}`;

  return withReactiveResponse<LtiConsumingPlatform>({
    url,
    method: 'GET',
    headers: new Headers({
      'Content-Type': 'application/json',
    }),
    op: LtiOperation.FindConsumingPlatform,
    cacheOptions: {
      key: url,
      types: [DataTypeLti.ConsumingPlatform],
    },
  });
}

export function buildCreateConsumingPlatform(platform: LtiConsumingPlatform) {
  const init = createRecord(platform, {});

  init.url = `${apis.systemsApi.host}${LTI_API_PATH}/platforms`;
  init.body = JSON.stringify(consumingPlatformRequestBody(platform));
  init.headers.set('Content-Type', 'application/json');

  return init;
}

export function buildUpdateConsumingPlatform(platform: LtiConsumingPlatform) {
  const platformId = platform.id;
  if (!platformId) {
    throw new Error('Cannot update a consuming platform without an id');
  }

  const init = updateRecord(platform, {});

  init.url = `${apis.systemsApi.host}${LTI_API_PATH}/platforms/${encodeURIComponent(platformId)}`;
  init.body = JSON.stringify(consumingPlatformRequestBody(platform));
  init.headers.set('Content-Type', 'application/json');

  return init;
}

export function buildDeleteConsumingPlatform(platform: LtiConsumingPlatform) {
  const platformId = platform.id;
  if (!platformId) {
    throw new Error('Cannot delete a consuming platform without an id');
  }

  const init = deleteRecord(platform, {});

  init.url = `${apis.systemsApi.host}${LTI_API_PATH}/platforms/${encodeURIComponent(platformId)}`;
  init.headers.set('Content-Type', 'application/json');

  return init;
}
